import React, { useState } from 'react';

import { usePathname } from 'next/navigation';

import useSWR from 'swr';

import { useCompose } from '@/context/ComposeContext';
import { useUserInfo } from '@/context/UserInfoContext';
import { ADMIN_INVITE_CREDITS_URL, getInviteCreditSummaries } from '@/lib/invites';

import { OmniBoxCollapsed } from './OmniBoxCollapsed';
import { OmniBoxExpanded } from './OmniBoxExpanded';
import { OmniBoxMenu } from './OmniBoxMenu';
import { useOmniBoxState } from './hooks/useOmniBoxState';

export const OmniBox: React.FC = () => {
  const { isExpanded, expand, collapse } = useOmniBoxState();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { userInfo } = useUserInfo();
  const { triggerComposeAtLatest } = useCompose();
  const pathname = usePathname();
  const { data: inviteCreditSummaries } = useSWR(
    userInfo?.is_admin ? ADMIN_INVITE_CREDITS_URL : null,
    getInviteCreditSummaries,
    {
      refreshInterval: 60_000,
      refreshWhenHidden: false,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    }
  );
  const inviteRequestCount =
    inviteCreditSummaries?.filter((summary) => summary.credits_requested_at !== null).length ?? 0;

  // Check if we're on a doc page
  const isDocPage = pathname?.startsWith('/doc/');

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleCollapsedClick = () => {
    if (isDocPage) {
      // On doc pages, trigger compose at the latest message
      triggerComposeAtLatest();
    } else {
      // Normal behavior on other pages
      expand();
    }
  };

  // Only show OmniBox for logged-in users
  if (!userInfo) {
    return null;
  }

  return (
    <>
      {!isExpanded && (
        <OmniBoxCollapsed
          onClick={handleCollapsedClick}
          onMenuClick={handleMenuClick}
          placeholder={isDocPage ? 'Reply' : 'Search or Ask'}
          inviteRequestCount={inviteRequestCount}
        />
      )}

      {isExpanded && <OmniBoxExpanded onClose={collapse} />}

      <OmniBoxMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        inviteRequestCount={inviteRequestCount}
      />
    </>
  );
};
